import { IStateInit } from "@gtmhub/core/routing";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { PermissionsService } from "@gtmhub/users";
import { StateParams, StateService } from "@uirouter/angularjs";
import { IPromise, IQService, IScope } from "angular";
import { IPermissionAction, IRole, RoleService } from "..";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";

export interface IPermissionsCtrlScope extends IScope {
  indicators: {
    loading: boolean;
    updating: boolean;
  };
  permissions: Array<IPermissionAction>;
  selectedPermissions: Map<string, boolean>;
  roleName: string;
  roleId: string;
  isSystem: boolean;
  params: { id?: string };
  toggleAllow(index: number);
  savePermissions();
}

export class PermissionsCtrl implements IStateInit {
  public static $inject = ["$scope", "$state", "$stateParams", "$q", "RoleService", "PermissionsService", "UIErrorHandlingService", "FeatureToggleV2Service"];

  constructor(
    private $scope: IPermissionsCtrlScope,
    private $stateService: StateService,
    private $stateParams: StateParams,
    private $q: IQService,
    private roleService: RoleService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private featureToggleService: FeatureToggleV2Service
  ) {
    this.$scope.params = { ...this.$stateParams };

    this.$scope.indicators = {
      loading: false,
      updating: false,
    };

    this.$scope.toggleAllow = this.toggleAllow;
    this.$scope.savePermissions = this.savePermissions;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators.loading = true;

    return this.$q
      .all([
        this.permissionsService.getPermissionActions(),
        this.permissionsService.getPermissionsByPrincipal(this.$scope.params.id),
        this.roleService.getRole(this.$scope.params.id),
        this.featureToggleService.isFeatureAvailable(FeatureFlag.EnableTagManagement),
      ])
      .then(
        (results) => {
          this.$scope.indicators.loading = false;
          let allActions = results[0];
          const featureAvailable: boolean = results[3];

          // hide Create Tags permission if feature flag is not activated
          if (!featureAvailable) {
            allActions = allActions.filter((item) => item.name != "CreateTags");
          }

          // converting the array of allowed actions for the user to hash for faster lookup
          const allowedActions = (results[1] as string[]).reduce((previousValue, currentValue) => {
            previousValue[currentValue] = true;
            return previousValue;
          }, {});

          const role = results[2] as IRole;
          this.$scope.roleName = role.name;
          this.$scope.roleId = role.id;

          if (!role.accountId) {
            this.$scope.isSystem = true;
          }

          const permissionActions: Array<IPermissionAction> = [];

          for (const action of allActions) {
            const permissionAction: IPermissionAction = {
              name: action.name,
              title: action.title,
              description: action.description,
              allowed: allowedActions[action.name] === true,
            };

            permissionActions.push(permissionAction);
          }

          this.$scope.permissions = permissionActions;
          this.$scope.selectedPermissions = allowedActions as Map<string, boolean>;
        },
        (err) => this.handleIndicatorError(err, "loading")
      );
  };

  private toggleAllow = (index) => {
    const currentSelectedPermission = this.$scope.permissions[index];
    currentSelectedPermission.allowed = !currentSelectedPermission.allowed;

    if (currentSelectedPermission.allowed === false) {
      this.$scope.selectedPermissions[currentSelectedPermission.name] = false;
    } else if (currentSelectedPermission.allowed === true) {
      this.$scope.selectedPermissions[currentSelectedPermission.name] = true;
    }
  };

  private savePermissions = () => {
    const allowedActions: string[] = [];
    const permissions = this.$scope.permissions;

    for (let i = 0; i < permissions.length; i++) {
      if (permissions[i].allowed) {
        allowedActions.push(permissions[i].name);
      }
    }

    /**
     * Because of the way the backend works, awarding a Badge is actually an Announcement.
     * We have removed the Announcements in the UI (in GVS-7292), but we have never had the chance to refactor
     * the backend and decouple these two features. For this reason, we must keep the Announcements working
     * in the background.
     *
     * Later, in GVS-11633, we also identified that if both permissions are not in sync, awarding Badges is
     * again impossible. The "if" below fixes this.
     *
     * For more info, see the comments in the forementioned tickets.
     */
    if (allowedActions.includes("ManageBadges")) {
      allowedActions.push("ManageAnnouncements");
    }

    this.$scope.indicators.updating = true;

    this.permissionsService.updatePrincipalPermissions(this.$scope.params.id, allowedActions).then(
      () => {
        this.$scope.indicators.updating = false;

        this.$stateService.go("gtmhub.roles");
      },
      (err) => this.handleIndicatorError(err, "updating", true)
    );
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };
}
